// Live progress of a background S3 synchronisation.
//
// A sync copies every source object through the service, which for a few GB
// runs for minutes. It used to block the request behind a spinner, so there
// was nothing to see and nothing to come back to. The job now runs server-side
// and this reads its progress, which means it also shows a sync that somebody
// else started, or one still running after a page reload.
import { useTranslation } from 'react-i18next';
import type { SyncResult } from '../api/types';
import { formatBytes } from '../lib/format';
import { syncFraction } from '../lib/syncProgress';
import type { Language } from '../i18n';

export function SyncProgress({ job, lang }: { job: SyncResult; lang: Language }) {
  const { t } = useTranslation();
  const fraction = syncFraction(job);
  const percent = Math.round(fraction * 100);
  const running = !job.done;

  const tone =
    job.status === 'failed'
      ? 'var(--danger)'
      : job.status === 'completed_partial'
        ? 'var(--warning)'
        : job.status === 'completed'
          ? 'var(--success)'
          : 'var(--accent)';

  return (
    <div style={{ marginTop: 12 }}>
      <div className="row between" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong style={{ fontSize: 14 }}>
          {running ? t('archives.syncRunning') : t(`archives.syncState.${job.status}`, t('archives.syncState.completed'))}
        </strong>
        <span className="muted" style={{ fontSize: 13 }}>
          {/* The totals are what makes this a progress bar rather than a
              spinner, so they are always spelled out next to it. */}
          {job.totalBytes > 0
            ? `${formatBytes(job.bytes ?? 0, lang)} / ${formatBytes(job.totalBytes, lang)} · ${percent}%`
            : t('archives.syncCounting')}
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={t('archives.syncProgressLabel')}
        style={{
          height: 8,
          borderRadius: 4,
          background: 'var(--surface-2)',
          overflow: 'hidden',
          marginTop: 6,
        }}
      >
        <div
          style={{
            width: `${Math.max(fraction > 0 ? 2 : 0, percent)}%`,
            height: '100%',
            background: tone,
            transition: 'width .4s ease',
          }}
        />
      </div>

      <p className="muted" style={{ fontSize: 13, margin: '6px 0 0' }}>
        {t('archives.syncObjects', {
          done: (job.imported ?? 0).toLocaleString(lang),
          total: (job.totalObjects ?? 0).toLocaleString(lang),
        })}
        {job.skipped ? ` · ${t('archives.syncSkipped', { count: job.skipped })}` : ''}
      </p>

      {job.error ? (
        <p style={{ fontSize: 13, margin: '6px 0 0', color: 'var(--danger)' }} role="alert">
          {job.error}
        </p>
      ) : null}
    </div>
  );
}
